import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { Reveal, Eyebrow } from './Section';
const faqs = [{
  q: '¿Necesito tener experiencia creando contenido?',
  a: 'No. El ebook está pensado para personas que empiezan desde cero. Vas a aprender cómo crear tus primeras muestras, armar un portfolio y presentar tus servicios aunque todavía no tengas clientes ni testimonios.'
}, {
  q: '¿Necesito tener miles de seguidores?',
  a: 'No. En UGC, las marcas contratan tu capacidad para crear contenido que puedan utilizar en sus propias redes, páginas de producto o campañas. Tu cantidad de seguidores no es lo más importante.'
}, {
  q: '¿Necesito una cámara profesional?',
  a: 'No. Podés comenzar con tu celular, buena iluminación y productos que ya tengas en tu casa. El ebook te enseña a mejorar la calidad visual de tus videos sin invertir en equipos costosos.'
}, {
  q: '¿Tengo que mostrar mi rostro?',
  a: 'No necesariamente. Podés crear contenido mostrando el producto, utilizando tus manos, grabando voz en off o incorporando textos en pantalla. Vas a conocer distintos formatos para elegir el que mejor se adapte a vos.'
}, {
  q: '¿Puedo empezar aunque nunca haya trabajado con una marca?',
  a: 'Sí. Aprenderás a crear muestras estratégicas sin esperar a que una empresa te contrate primero. Así podrás construir un portfolio que demuestre lo que sabés hacer y empezar a presentarte profesionalmente.'
}, {
  q: '¿Realmente puedo cobrar en dólares?',
  a: 'Sí, podés ofrecer tus servicios en dólares a marcas nacionales e internacionales. El ebook te ayuda a definir paquetes, presentar precios y comprender qué aspectos deberías cotizar para no cobrar al azar ni regalar tu trabajo.'
}, {
  q: '¿Voy a ganar USD 1.000 en siete días?',
  a: 'No se promete que vas a generar USD 1.000 en siete días. Durante esos siete días vas a construir las bases: portfolio, servicios, precios y un sistema para contactar marcas. Los resultados económicos dependerán de tu aplicación, constancia, calidad del contenido y capacidad para conseguir clientes.'
}, {
  q: '¿El ebook me dice dónde encontrar marcas y qué escribirles?',
  a: 'Sí. Vas a aprender cómo identificar marcas, organizar una lista de más de 80 posibles clientes, enviar mensajes profesionales y realizar seguimientos sin sentir que estás pidiendo un favor.'
}, {
  q: '¿Cuánto tiempo necesito para aplicar el contenido?',
  a: 'Podés avanzar siguiendo el plan de siete días y adaptarlo a tus horarios. No necesitás dedicar jornadas completas, pero sí reservar tiempo para leer, grabar muestras, preparar tu portfolio y contactar marcas.'
}, {
  q: '¿Qué pasa si compro el ebook y siento que no es para mí?',
  a: 'Tenés una garantía de siete días corridos desde la compra. Durante ese período podés revisar el contenido y comprobar si te brinda la claridad y las herramientas que necesitabas para comenzar. Si considerás que no cumple con lo prometido, podés solicitar la devolución según las condiciones de la garantía.'
}];
function Item({
  q,
  a,
  index
}: {
  q: string;
  a: string;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <div className="overflow-hidden rounded-2xl border border-sand bg-white" data-id="element-36" data-mp="cg6zrdwagax1">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left" data-id="element-37" data-mp="hfa5l0k3el62">
        <span className="font-medium text-plum" data-id="element-38" data-mp="mfxwlm8bn20a">{q}</span>
        <PlusIcon className={`h-5 w-5 shrink-0 text-coral transition-transform duration-300 ${open ? 'rotate-45' : ''}`} data-id="element-39" data-mp="qgzx06dz5k3u" />
      </button>
      <AnimatePresence initial={false} data-id="element-40" data-mp="2s7uv54vnlvdq">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-41" data-mp="2s9fqgwpslicn">
            <p className="px-5 pb-5 text-sm leading-relaxed text-ink/75" data-id="element-42" data-mp="1wvv1b64gsowg">{a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  return <section className="w-full bg-cream py-16 sm:py-24" data-id="element-43" data-mp="26uj9ld6ndpmy">
      <div className="mx-auto max-w-3xl px-5 sm:px-6" data-id="element-44" data-mp="26szp1adyksfd">
        <Reveal className="text-center" data-id="element-45" data-mp="1j6jiud9c5z8d">
          <Eyebrow data-id="element-46" data-mp="1j3dtcwwsovc4">Preguntas frecuentes</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-plum sm:text-4xl" data-id="element-47" data-mp="1e76l2xgxf7r0">
            Resolvemos tus dudas antes de empezar
          </h2>
        </Reveal>

        <div className="mt-10 space-y-3" data-id="element-48" data-mp="1o41i1bxjx036">
          {faqs.map((f, i) => <Reveal key={f.q} delay={i * 0.03} data-id="element-49" data-mp="10f0g87j84n6v" data-mpi={i}>
              <Item q={f.q} a={f.a} index={i} data-id="element-50" data-mp="10dpcu8toxhga" />
            </Reveal>)}
        </div>

        <Reveal delay={0.1} data-id="element-51" data-mp="veulujlkkml3">
          <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl border border-gold/30 bg-gold-soft/25 p-6 sm:flex-row sm:items-center" data-id="element-52" data-mp="vfnkexadk5s5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold text-white" data-id="element-53" data-mp="3buhi2scpychp">
              <ShieldCheckIcon className="h-6 w-6" data-id="element-54" data-mp="2x64i7kaig9hx" />
            </span>
            <div data-id="element-55" data-mp="323wj33t4v5y9">
              <h3 className="font-display text-lg font-semibold text-plum" data-id="element-56" data-mp="323egzqjshxif">
                Garantía “Primeros pasos sin riesgo” — 7 días
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-ink/75" data-id="element-57" data-mp="3prxnq1n6y28e">
                Tenés 7 días corridos desde tu compra para revisar el método. Si sentís que el
                contenido no te ayudó a entender cómo crear tu portfolio, definir tus servicios y
                contactar marcas, podés solicitar la devolución. Sin preguntas incómodas y sin
                riesgos innecesarios.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}